import {KTSVG} from '../../../_metronic/helpers'
import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useAuth} from '../../../app/modules/auth'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useCreateSupplier} from '../../contextProviders/Atomqueryprovider'

type Props = {
  show: boolean
  handleClose: () => void
}

const initialValues = {
  supplier_firstname: "",
  supplier_secondname: "",
  supplier_phone_number: "",
  supplier_email: "",
  supplier_payment_details: "",
  supplier_company_name: "",
  supplier_address: ""
}

const supplierSchema = Yup.object().shape({
  supplier_firstname: Yup.string()
    .min(2, 'Minimum 2 characters')
    .max(50, 'Maximum 50 characters')
    .required('First name is required'),
  supplier_secondname: Yup.string()
    .min(2, 'Minimum 2 characters')
    .max(50, 'Maximum 50 characters')
    .required('Second name is required'),
  supplier_phone_number: Yup.string()
    .matches(/^[0-9+\-\s()]+$/, 'Invalid phone number format')
    .min(10, 'Phone number too short')
    .max(15, 'Phone number too long'),
  supplier_email: Yup.string()
    .email('Invalid email format')
    .max(100, 'Email too long'),
  supplier_payment_details: Yup.string()
    .max(200, 'Payment details too long'),
  supplier_company_name: Yup.string()
    .max(150, 'Company name too long'),
  supplier_address: Yup.string()
    .max(255, 'Address too long')
})

export const AddSupplier: React.FC<Props> = ({show, handleClose}) => {
  const [loading, setLoading] = useState(false)
  const [supplierData, setSupplierData] = useState<any>({})
  const [submittingready, setSubmittingready] = useState<boolean>(false)
  const [isAutoEmail, setIsAutoEmail] = useState<boolean>(true)

  const {currentUser} = useAuth()

  const {mutateAsync: createSupplierMutateAsync} = useCreateSupplier(supplierData)

  const generateEmail = (firstname: string, secondname: string, company: string): string => {
    let base = ''
    if (company) {
      base = company.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '')
    } else {
      const first = firstname.toLowerCase().replace(/[^a-z0-9]/g, '')
      const second = secondname.toLowerCase().replace(/[^a-z0-9]/g, '')
      base = first + (second ? '.' + second : '')
    }
    return base + '@suppliers.com'
  }

  const formik = useFormik({
    initialValues,
    validationSchema: supplierSchema,
    validateOnBlur: true,
    onSubmit: async (values, {setStatus, setSubmitting, resetForm}) => {
      setLoading(true)
      
      setSupplierData({
        user_id: currentUser?.user_id,
        supplier_firstname: values.supplier_firstname,
        supplier_secondname: values.supplier_secondname,
        supplier_phone_number: values.supplier_phone_number || null,
        supplier_email: values.supplier_email || null,
        supplier_payment_details: values.supplier_payment_details || null,
        supplier_company_name: values.supplier_company_name || null,
        supplier_address: values.supplier_address || null
      })
      setSubmittingready(true)
    },
  })

  useEffect(() => {
    if (isAutoEmail && formik.values.supplier_firstname && formik.values.supplier_secondname) {
      const email = generateEmail(
        formik.values.supplier_firstname,
        formik.values.supplier_secondname,
        formik.values.supplier_company_name
      )
      formik.setFieldValue('supplier_email', email)
    }
  }, [formik.values.supplier_firstname, formik.values.supplier_secondname, formik.values.supplier_company_name, isAutoEmail])

  useEffect(() => {
    if (submittingready && supplierData) {
      createSupplierMutateAsync(supplierData, {
        onSuccess: () => {
          setLoading(false)
          formik.resetForm({})
          formik.setSubmitting(false)
          setSupplierData({})
          setSubmittingready(false)
          setIsAutoEmail(true)
          handleClose()
        },
        onError(error, variables, context) {
          formik.setStatus('The supplier details are incorrect')
          formik.setSubmitting(false)
          setLoading(false)
          setSubmittingready(false)
        },
      })
    }
  }, [submittingready])

  const handleNameChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(field, e.target.value.toUpperCase())
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue('supplier_email', e.target.value)
    setIsAutoEmail(false)
  }

  return (
    <Modal
      className='modal fade'
      id='kt_modal_add_supplier'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      centered
      dialogClassName='modal-lg'
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='fw-bolder'>Add Supplier</h3>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <form
            id='kt_modal_add_supplier_form'
            noValidate
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            onSubmit={formik.handleSubmit}
          >
            {/* Name Fields Row */}
            <div className='row fv-row mb-7'>
              <div className='col-xl-6'>
                <div className='fv-row mb-7'>
                  <label className='required fw-bold fs-6 mb-2'>First Name</label>
                  <input
                    placeholder='Enter first name'
                    type='text'
                    value={formik.values.supplier_firstname}
                    onChange={handleNameChange('supplier_firstname')}
                    autoComplete='off'
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {
                        'is-invalid': formik.touched.supplier_firstname && formik.errors.supplier_firstname,
                      },
                      {
                        'is-valid': formik.touched.supplier_firstname && !formik.errors.supplier_firstname,
                      }
                    )}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {formik.touched.supplier_firstname && formik.errors.supplier_firstname && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.supplier_firstname}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='col-xl-6'>
                <div className='fv-row mb-7'>
                  <label className='required fw-bold fs-6 mb-2'>Second Name</label>
                  <input
                    placeholder='Enter second name'
                    type='text'
                    value={formik.values.supplier_secondname}
                    onChange={handleNameChange('supplier_secondname')}
                    autoComplete='off'
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {
                        'is-invalid': formik.touched.supplier_secondname && formik.errors.supplier_secondname,
                      },
                      {
                        'is-valid': formik.touched.supplier_secondname && !formik.errors.supplier_secondname,
                      }
                    )}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {formik.touched.supplier_secondname && formik.errors.supplier_secondname && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.supplier_secondname}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Fields Row */}
            <div className='row fv-row mb-7'>
              <div className='col-xl-6'>
                <div className='fv-row mb-7'>
                  <label className='fw-bold fs-6 mb-2'>Phone Number</label>
                  <input
                    placeholder='+254 XXX XXX XXX'
                    type='tel'
                    {...formik.getFieldProps('supplier_phone_number')}
                    autoComplete='off'
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {
                        'is-invalid': formik.touched.supplier_phone_number && formik.errors.supplier_phone_number,
                      },
                      {
                        'is-valid': formik.touched.supplier_phone_number && !formik.errors.supplier_phone_number,
                      }
                    )}
                  />
                  {formik.touched.supplier_phone_number && formik.errors.supplier_phone_number && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.supplier_phone_number}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className='col-xl-6'>
                <div className='fv-row mb-7'>
                  <label className='fw-bold fs-6 mb-2'>Email Address {!isAutoEmail && <small className="text-muted">(Auto-generated disabled)</small>}</label>
                  <input
                    placeholder='supplier@example.com'
                    type='email'
                    value={formik.values.supplier_email}
                    onChange={handleEmailChange}
                    autoComplete='off'
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {
                        'is-invalid': formik.touched.supplier_email && formik.errors.supplier_email,
                      },
                      {
                        'is-valid': formik.touched.supplier_email && !formik.errors.supplier_email,
                      }
                    )}
                  />
                  {formik.touched.supplier_email && formik.errors.supplier_email && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.supplier_email}</span>
                      </div>
                    </div>
                  )}
                  {isAutoEmail && formik.values.supplier_email && (
                    <small className="text-muted">Auto-generated: {formik.values.supplier_email}</small>
                  )}
                </div>
              </div>
            </div>

            {/* Company Name Field */}
            <div className='fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>Company Name</label>
              <input
                placeholder='Enter company name (optional)'
                type='text'
                value={formik.values.supplier_company_name}
                onChange={handleNameChange('supplier_company_name')}
                autoComplete='off'
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.supplier_company_name && formik.errors.supplier_company_name,
                  },
                  {
                    'is-valid': formik.touched.supplier_company_name && !formik.errors.supplier_company_name,
                  }
                )}
                style={{ textTransform: 'uppercase' }}
              />
              {formik.touched.supplier_company_name && formik.errors.supplier_company_name && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.supplier_company_name}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Details Field */}
            <div className='fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>Payment Details</label>
              <textarea
                placeholder='Enter payment details (bank account, mobile money, etc.)'
                {...formik.getFieldProps('supplier_payment_details')}
                autoComplete='off'
                rows={3}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.supplier_payment_details && formik.errors.supplier_payment_details,
                  },
                  {
                    'is-valid': formik.touched.supplier_payment_details && !formik.errors.supplier_payment_details,
                  }
                )}
              />
              {formik.touched.supplier_payment_details && formik.errors.supplier_payment_details && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.supplier_payment_details}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Address Field */}
            <div className='fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>Address</label>
              <textarea
                placeholder='Enter supplier address (optional)'
                {...formik.getFieldProps('supplier_address')}
                autoComplete='off'
                rows={3}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.supplier_address && formik.errors.supplier_address,
                  },
                  {
                    'is-valid': formik.touched.supplier_address && !formik.errors.supplier_address,
                  }
                )}
              />
              {formik.touched.supplier_address && formik.errors.supplier_address && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.supplier_address}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Status Message */}
            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}
          </form>
        </div>
        
        <div className='modal-footer border-0 mt-n5'>
          <button type='button' className='btn btn-light-primary' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='submit'
            form='kt_modal_add_supplier_form'
            className='btn btn-success'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Add Supplier</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}
